import axios from "axios";
import { getStoredToken } from "./auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Attach gate token to every request
api.interceptors.request.use((config) => {
  const t = getStoredToken();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// On 401, clear the stored token so the UI redirects to /login
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      const detail = err?.response?.data?.detail;
      if (detail === "auth_required" || detail === "invalid_or_expired") {
        try {
          localStorage.removeItem("wfcGateToken");
        } catch {
          // ignore storage errors
        }
        // Force reload so AuthProvider picks up the change
        if (!window.location.pathname.startsWith("/share/") &&
            !window.location.pathname.startsWith("/confirm/") &&
            window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(err);
  }
);

export const plansApi = {
  get: (projectId) =>
    api.get(`/projects/${projectId}/plan`).then((r) => r.data),
  updatePlan: (projectId, data) =>
    api.patch(`/projects/${projectId}/plan`, data).then((r) => r.data),
  createTask: (projectId, data) =>
    api.post(`/projects/${projectId}/plan/tasks`, data).then((r) => r.data),
  updateTask: (taskId, data) =>
    api.patch(`/plan-tasks/${taskId}`, data).then((r) => r.data),
  removeTask: (taskId) =>
    api.delete(`/plan-tasks/${taskId}`).then((r) => r.data),
  createDependency: (projectId, fromId, toId, type = "FS") =>
    api
      .post(`/projects/${projectId}/plan/dependencies`, {
        from_id: fromId,
        to_id: toId,
        type,
      })
      .then((r) => r.data),
  removeDependency: (depId) =>
    api.delete(`/plan-dependencies/${depId}`).then((r) => r.data),
  listConflicts: (projectId) =>
    api.get(`/projects/${projectId}/plan/conflicts`).then((r) => r.data),
  dismissConflict: (conflictId) =>
    api.post(`/plan-conflicts/${conflictId}/dismiss`).then((r) => r.data),
  resolveConflict: (conflictId, action) =>
    api
      .post(`/plan-conflicts/${conflictId}/resolve`, { action })
      .then((r) => r.data),
  levelPlan: (projectId) =>
    api.post(`/projects/${projectId}/plan/level`).then((r) => r.data),
  revertMoves: (projectId, moves) =>
    api
      .post(`/projects/${projectId}/plan/revert-moves`, { moves })
      .then((r) => r.data),
};

// Nudge API — gentle producer-confirmed reminder emails (Feb 2026)
export const nudgeApi = {
  preview: (taskId) =>
    api.get(`/plan-tasks/${taskId}/nudge/preview`).then((r) => r.data),
  send: (taskId, { subject, body, force = false }) =>
    api
      .post(`/plan-tasks/${taskId}/nudge`, { subject, body, force })
      .then((r) => r.data),
  // Jul 2026 — audit log for the small "Nudged Nx" hint on the task dialog.
  history: (taskId) =>
    api.get(`/plan-tasks/${taskId}/nudge/history`).then((r) => r.data),
};

// My Tasks — cross-project personal feed (Feb 2026)
export const myTasksApi = {
  list: ({ includeDone = false } = {}) =>
    api
      .get(`/my-tasks`, { params: { include_done: includeDone } })
      .then((r) => r.data),
};

// Waiting On — inverse of My Tasks. Tasks I created + assigned to someone
// else that are still open (Iter 47).
export const waitingOnApi = {
  list: () => api.get(`/waiting-on`).then((r) => r.data),
};

// Global working-hours settings (Feb 2026, Phase A of time-aware scheduling)
export const settingsApi = {
  getWorkingConfig: () =>
    api.get(`/admin/settings/working-config`).then((r) => r.data),
  saveWorkingConfig: (payload) =>
    api.put(`/admin/settings/working-config`, payload).then((r) => r.data),
};

export const studioApi = {
  // Cross-project resource manager (admin only).
  // params: { start, end, project_ids?: csv, user_ids?: csv, capacity_hours_per_day? }
  getResources: (params = {}) =>
    api.get("/studio/resources", { params }).then((r) => r.data),
  // Phase 1.7 — Share Links (admin only for CRUD, public for /public/:token)
  createShareLink: (data) =>
    api.post("/studio/share-links", data).then((r) => r.data),
  listShareLinks: () => api.get("/studio/share-links").then((r) => r.data),
  revokeShareLink: (id) =>
    api.delete(`/studio/share-links/${id}`).then((r) => r.data),
  // Public share fetch — pass through plain axios so no Authorization header
  // gets attached (some browsers + reverse proxies treat the public response
  // as cacheable). The shared interceptor is harmless here either way, but
  // we still use a clean instance for safety.
  getPublicResources: (token) => {
    return axios
      .get(`${API}/studio/public/${encodeURIComponent(token)}`)
      .then((r) => r.data);
  },
};

// Phase 1.6 — Plan Templates (admin authors, editors apply)
export const templatesApi = {
  list: () => api.get("/plan-templates").then((r) => r.data),
  get: (id) => api.get(`/plan-templates/${id}`).then((r) => r.data),
  create: (data) => api.post("/plan-templates", data).then((r) => r.data),
  update: (id, data) => api.patch(`/plan-templates/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/plan-templates/${id}`).then((r) => r.data),
  snapshotFromProject: (projectId, data) =>
    api
      .post(`/projects/${projectId}/plan/snapshot-template`, data)
      .then((r) => r.data),
  apply: (templateId, projectId, anchorDate) =>
    api
      .post(`/plan-templates/${templateId}/apply`, {
        project_id: projectId,
        anchor_date: anchorDate,
      })
      .then((r) => r.data),
};

export const projectsApi = {
  list: () => api.get("/projects").then((r) => r.data),
  get: (id) => api.get(`/projects/${id}`).then((r) => r.data),
  create: (data) => api.post("/projects", data).then((r) => r.data),
  update: (id, data) => api.patch(`/projects/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/projects/${id}`).then((r) => r.data),
  gearCheckoutStatus: (id) =>
    api.get(`/projects/${id}/gear-checkout-status`).then((r) => r.data),
  complete: (id) =>
    api.post(`/projects/${id}/call-sheets/complete`).then((r) => r.data),
};

// Productivity Pulse + Weekly Digest (Feb 2026)
export const launchpadApi = {
  getPulse: (days = 7) =>
    api.get("/launchpad/productivity-pulse", { params: { days } }).then((r) => r.data),
  sendWeeklyDigest: ({ dryRun = false, skipEmpty = true } = {}) =>
    api
      .post("/admin/digest/send-weekly", null, {
        params: { dry_run: dryRun, skip_empty: skipEmpty },
      })
      .then((r) => r.data),
  getAtRisk: (limit = 8, assigneeUserId = null) => {
    const params = { limit };
    if (assigneeUserId) params.assignee_user_id = assigneeUserId;
    return api.get("/launchpad/at-risk", { params }).then((r) => r.data);
  },
  getStudioInsights: ({ days = 14, limit = 2 } = {}) =>
    api.get("/studio/insights", { params: { days, limit } }).then((r) => r.data),
  getTeamCapacity: ({ weekStart = null, userId = null } = {}) => {
    const params = {};
    if (weekStart) params.week_start = weekStart;
    if (userId) params.user_id = userId;
    return api
      .get("/launchpad/team-capacity", { params })
      .then((r) => r.data);
  },
  getTeamRoster: () =>
    api.get("/launchpad/team-roster").then((r) => r.data),
};

// Personal Stickies (Feb 2026)
export const personalTodosApi = {
  list: () => api.get("/personal-todos").then((r) => r.data),
  create: (text) => api.post("/personal-todos", { text }).then((r) => r.data),
  toggle: (id, completed) =>
    api.patch(`/personal-todos/${id}`, { completed }).then((r) => r.data),
  remove: (id) => api.delete(`/personal-todos/${id}`).then((r) => r.data),
  clearCompleted: () =>
    api.delete("/personal-todos/completed/clear").then((r) => r.data),
};

// Task Comments (Feb 2026)
export const taskCommentsApi = {
  list: (taskId) =>
    api.get(`/plan-tasks/${taskId}/comments`).then((r) => r.data),
  create: (taskId, text, mentions = [], parentId = null) =>
    api
      .post(`/plan-tasks/${taskId}/comments`, {
        text,
        mentions,
        parent_id: parentId,
      })
      .then((r) => r.data),
  edit: (commentId, text, mentions = []) =>
    api.patch(`/task-comments/${commentId}`, { text, mentions }).then((r) => r.data),
  remove: (commentId) =>
    api.delete(`/task-comments/${commentId}`).then((r) => r.data),
  counts: (projectId) =>
    api
      .get("/plan-tasks/comment-counts", { params: { project_id: projectId } })
      .then((r) => r.data),
  discussion: (projectId, limit = 200) =>
    api
      .get(`/projects/${projectId}/discussion`, { params: { limit } })
      .then((r) => r.data),
};

// Project Posts (Feb 2026) — general discussion items, not pinned to a task
export const projectPostsApi = {
  list: (projectId) =>
    api.get(`/projects/${projectId}/posts`).then((r) => r.data),
  create: (projectId, text, mentions = [], parentId = null) =>
    api
      .post(`/projects/${projectId}/posts`, {
        text,
        mentions,
        parent_id: parentId,
      })
      .then((r) => r.data),
  edit: (postId, text, mentions = []) =>
    api.patch(`/project-posts/${postId}`, { text, mentions }).then((r) => r.data),
  remove: (postId) =>
    api.delete(`/project-posts/${postId}`).then((r) => r.data),
};

export const shotsApi = {
  list: (projectId) =>
    api.get(`/projects/${projectId}/shots`).then((r) => r.data),
  create: (projectId, data) =>
    api.post(`/projects/${projectId}/shots`, data).then((r) => r.data),
  bulkCreate: (projectId, shots) =>
    api.post(`/projects/${projectId}/shots/bulk`, { shots }).then((r) => r.data),
  update: (shotId, data) =>
    api.patch(`/shots/${shotId}`, data).then((r) => r.data),
  remove: (shotId) => api.delete(`/shots/${shotId}`).then((r) => r.data),
  duplicate: (shotId) =>
    api.post(`/shots/${shotId}/duplicate`).then((r) => r.data),
  reorder: (projectId, shotIds) =>
    api
      .post(`/projects/${projectId}/shots/reorder`, { shot_ids: shotIds })
      .then((r) => r.data),
  bulkUpdate: (projectId, shotIds, patch) =>
    api
      .post(`/projects/${projectId}/shots/bulk-update`, { shot_ids: shotIds, ...patch })
      .then((r) => r.data),
  bulkDelete: (projectId, shotIds) =>
    api
      .post(`/projects/${projectId}/shots/bulk-delete`, { shot_ids: shotIds })
      .then((r) => r.data),
  uploadImage: (shotId, dataUri) =>
    api.post(`/shots/${shotId}/image`, { image_data: dataUri }).then((r) => r.data),
  aiSuggest: (projectId, sceneDescription, numShots = 5) =>
    api
      .post(`/projects/${projectId}/ai-suggest`, {
        scene_description: sceneDescription,
        num_shots: numShots,
      })
      .then((r) => r.data),
};

export const locationsApi = {
  list: () => api.get("/locations").then((r) => r.data),
  create: (data) => api.post("/locations", data).then((r) => r.data),
  update: (id, data) => api.patch(`/locations/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/locations/${id}`).then((r) => r.data),
  findHospital: (id) =>
    api.post(`/locations/${id}/find-hospital`).then((r) => r.data),
};

export const teamApi = {
  list: () => api.get("/team").then((r) => r.data),
  create: (data) => api.post("/team", data).then((r) => r.data),
  update: (id, data) => api.patch(`/team/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/team/${id}`).then((r) => r.data),
};

export const weeklyUpdatesApi = {
  // Admin: all updates across projects, newest week first.
  listAll: (weekEnding) =>
    api
      .get("/weekly-updates", { params: weekEnding ? { week_ending: weekEnding } : {} })
      .then((r) => r.data),
  // Admin: distinct week_ending values so the UI can build a week picker.
  listWeeks: () => api.get("/weekly-updates/weeks").then((r) => r.data),
  // Any member: updates for a single project.
  listForProject: (projectId) =>
    api.get(`/projects/${projectId}/weekly-updates`).then((r) => r.data),
  // Admin: trigger the batch now. Returns a per-project status roll-up.
  generateNow: () => api.post("/weekly-updates/generate").then((r) => r.data),
  // Admin: preview which projects would be included without paying the LLM.
  dryRun: () => api.post("/weekly-updates/dry-run").then((r) => r.data),
};


export const callSheetApi = {
  listAll: () => api.get("/call-sheets").then((r) => r.data),
  // Legacy single-day (Day 1) endpoints — kept for backward compat
  get: (projectId) =>
    api.get(`/projects/${projectId}/call-sheet`).then((r) => r.data),
  update: (projectId, data) =>
    api.patch(`/projects/${projectId}/call-sheet`, data).then((r) => r.data),
  pdfUrl: (projectId) => {
    const t = getStoredToken();
    const qs = t ? `?token=${encodeURIComponent(t)}` : "";
    return `${API}/projects/${projectId}/call-sheet/pdf${qs}`;
  },
  email: (projectId, payload) =>
    api
      .post(`/projects/${projectId}/call-sheet/email`, {
        ...payload,
        confirm_base_url: window.location.origin,
      })
      .then((r) => r.data),
  // Multi-day endpoints
  listForProject: (projectId) =>
    api.get(`/projects/${projectId}/call-sheets`).then((r) => r.data),
  getDay: (projectId, dayId) =>
    api.get(`/projects/${projectId}/call-sheets/${dayId}`).then((r) => r.data),
  updateDay: (projectId, dayId, data) =>
    api
      .patch(`/projects/${projectId}/call-sheets/${dayId}`, data)
      .then((r) => r.data),
  addDay: (projectId, body = {}) =>
    api.post(`/projects/${projectId}/call-sheets`, body).then((r) => r.data),
  removeDay: (projectId, dayId) =>
    api
      .delete(`/projects/${projectId}/call-sheets/${dayId}`)
      .then((r) => r.data),
  pdfUrlForDay: (projectId, dayId) => {
    const t = getStoredToken();
    const qs = t ? `?token=${encodeURIComponent(t)}` : "";
    return `${API}/projects/${projectId}/call-sheets/${dayId}/pdf${qs}`;
  },
  emailDay: (projectId, dayId, payload) =>
    api
      .post(`/projects/${projectId}/call-sheets/${dayId}/email`, {
        ...payload,
        confirm_base_url: window.location.origin,
      })
      .then((r) => r.data),
  // Archive / Complete Shoot
  listArchivedForProject: (projectId) =>
    api
      .get(`/projects/${projectId}/call-sheets/archived`)
      .then((r) => r.data),
  completeDay: (projectId, dayId) =>
    api
      .post(`/projects/${projectId}/call-sheets/${dayId}/complete`)
      .then((r) => r.data),
  wrapAll: (projectId) =>
    api.post(`/projects/${projectId}/call-sheets/wrap`).then((r) => r.data),
  unarchiveDay: (projectId, dayId) =>
    api
      .post(`/projects/${projectId}/call-sheets/${dayId}/unarchive`)
      .then((r) => r.data),
  // Shared
  confirm: (callSheetId, token) =>
    api
      .post(`/call-sheets/${callSheetId}/confirm/${token}`)
      .then((r) => r.data),
  publicGet: (callSheetId) =>
    api.get(`/public/call-sheets/${callSheetId}`).then((r) => r.data),
};

export const webhooksApi = {
  list: () => api.get("/webhooks").then((r) => r.data),
  get: (id) => api.get(`/webhooks/${id}`).then((r) => r.data),
  create: (data) => api.post("/webhooks", data).then((r) => r.data),
  update: (id, data) => api.patch(`/webhooks/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/webhooks/${id}`).then((r) => r.data),
  test: (id) => api.post(`/webhooks/${id}/test`).then((r) => r.data),
  rotateSecret: (id) =>
    api.post(`/webhooks/${id}/rotate-secret`).then((r) => r.data),
  deliveries: (id) =>
    api.get(`/webhooks/${id}/deliveries`).then((r) => r.data),
};

// System integrations (env-configured webhooks like the videos site notifier).
// Admin-only. Powers the "System Integrations" card on the Webhooks page.
export const systemWebhooksApi = {
  list: () => api.get("/system-webhooks").then((r) => r.data),
  test: (key) => api.post(`/system-webhooks/${key}/test`).then((r) => r.data),
  deliveries: (key) =>
    api.get(`/system-webhooks/${key}/deliveries`).then((r) => r.data),
};

export const usersApi = {
  list: () => api.get("/users").then((r) => r.data),
  create: (data) => api.post("/users", data).then((r) => r.data),
  update: (id, data) => api.patch(`/users/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/users/${id}`).then((r) => r.data),
  projectAssignments: () =>
    api.get("/admin/users/project-assignments").then((r) => r.data),
};

// Pending workspace + project invites (Jul 2026)
export const invitesApi = {
  list: (projectId = null) =>
    api
      .get("/invites", { params: projectId ? { project_id: projectId } : {} })
      .then((r) => r.data),
  resend: (id) =>
    api.post(`/invites/${encodeURIComponent(id)}/resend`).then((r) => r.data),
  revoke: (id) =>
    api.delete(`/invites/${encodeURIComponent(id)}`).then((r) => r.data),
};

export const clientsApi = {
  list: () => api.get("/clients").then((r) => r.data),
  get: (id) => api.get(`/clients/${id}`).then((r) => r.data),
  create: (data) => api.post("/clients", data).then((r) => r.data),
  update: (id, data) => api.patch(`/clients/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/clients/${id}`).then((r) => r.data),
  projects: (id) => api.get(`/clients/${id}/projects`).then((r) => r.data),
  journal: {
    list: (id) => api.get(`/clients/${id}/journal`).then((r) => r.data),
    create: (id, body) =>
      api.post(`/clients/${id}/journal`, { body }).then((r) => r.data),
    update: (id, entryId, body) =>
      api.patch(`/clients/${id}/journal/${entryId}`, { body }).then((r) => r.data),
    remove: (id, entryId) =>
      api.delete(`/clients/${id}/journal/${entryId}`).then((r) => r.data),
  },
};


export const projectMembersApi = {
  list: (projectId) =>
    api.get(`/projects/${projectId}/members`).then((r) => r.data),
  invitableUsers: (projectId) =>
    api.get(`/projects/${projectId}/invitable-users`).then((r) => r.data),
  invite: (projectId, data) =>
    api
      .post(`/projects/${projectId}/members`, { ...data, project_id: projectId })
      .then((r) => r.data),
  updateRole: (projectId, userId, role) =>
    api
      .patch(`/projects/${projectId}/members/${userId}`, { role })
      .then((r) => r.data),
  remove: (projectId, userId) =>
    api
      .delete(`/projects/${projectId}/members/${userId}`)
      .then((r) => r.data),
};

export const messagesApi = {
  send: (projectId, data) =>
    api.post(`/projects/${projectId}/message-members`, data).then((r) => r.data),
};

// ────────────────────────── Job Expenses (Feb 2026) ───────────────────
// Multipart create/update (receipt upload); JSON everywhere else.
export const expensesApi = {
  list: (projectId) =>
    api.get(`/projects/${projectId}/expenses`).then((r) => r.data),
  create: (projectId, formData) =>
    api
      .post(`/projects/${projectId}/expenses`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  update: (projectId, expenseId, formData) =>
    api
      .patch(`/projects/${projectId}/expenses/${expenseId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  remove: (projectId, expenseId) =>
    api
      .delete(`/projects/${projectId}/expenses/${expenseId}`)
      .then((r) => r.data),
  markReimbursed: (projectId, expenseId) =>
    api
      .post(`/projects/${projectId}/expenses/${expenseId}/mark-reimbursed`)
      .then((r) => r.data),
  unmarkReimbursed: (projectId, expenseId) =>
    api
      .post(`/projects/${projectId}/expenses/${expenseId}/unmark-reimbursed`)
      .then((r) => r.data),
  // Auth-gated file download — use `receiptUrl` for a direct <a> (relies on
  // the axios interceptor's cookie auth), OR fetch as blob when you need
  // to inject an Authorization header manually.
  receiptUrl: (projectId, expenseId) =>
    `${api.defaults.baseURL}/projects/${projectId}/expenses/${expenseId}/receipt`,
  // Mint a 5-minute HMAC-signed URL that can be opened directly in a new
  // browser tab without a session cookie — used by the "View" link on the
  // expense table because target=_blank drops the auth header.
  receiptSignedUrl: (projectId, expenseId) =>
    api
      .post(`/projects/${projectId}/expenses/${expenseId}/receipt/signed-url`)
      .then((r) => r.data),
  downloadPdf: (projectId) =>
    api
      .get(`/projects/${projectId}/expenses/pdf`, { responseType: "blob" })
      .then((r) => r.data),
  pendingSummary: () =>
    api.get(`/expenses/pending-summary`).then((r) => r.data),
  sendTestWeeklyEmail: () =>
    api.post(`/expenses/weekly-email/send-test`).then((r) => r.data),
};

// ────────────────────────── Planning Calendar / Ideas (Feb 2026) ─────
// Ideas are soft-projects. Create/PATCH use multipart because the create
// path optionally accepts attachments in the same request.
export const ideasApi = {
  list: (clientId) =>
    api
      .get(`/ideas`, { params: clientId ? { client_id: clientId } : {} })
      .then((r) => r.data),
  get: (id) => api.get(`/ideas/${id}`).then((r) => r.data),
  create: (formData) =>
    api
      .post(`/ideas`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  update: (id, formData) =>
    api
      .patch(`/ideas/${id}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  remove: (id) => api.delete(`/ideas/${id}`).then((r) => r.data),
  addAttachment: (id, formData) =>
    api
      .post(`/ideas/${id}/attachments`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  removeAttachment: (id, attId) =>
    api.delete(`/ideas/${id}/attachments/${attId}`).then((r) => r.data),
  attachmentSignedUrl: (id, attId) =>
    api
      .post(`/ideas/${id}/attachments/${attId}/signed-url`)
      .then((r) => r.data),
  launch: (id) => api.post(`/ideas/${id}/launch`).then((r) => r.data),
};

export const ideaVideoStylesApi = {
  list: (includeArchived = false) =>
    api
      .get(`/idea-video-styles`, { params: includeArchived ? { include_archived: true } : {} })
      .then((r) => r.data),
  create: (name) =>
    api.post(`/idea-video-styles`, { name }).then((r) => r.data),
  update: (id, patch) =>
    api.patch(`/idea-video-styles/${id}`, patch).then((r) => r.data),
  remove: (id) => api.delete(`/idea-video-styles/${id}`).then((r) => r.data),
  suggestions: () =>
    api.get(`/idea-video-styles/suggestions`).then((r) => r.data),
  promote: (name) =>
    api.post(`/idea-video-styles/promote`, { name }).then((r) => r.data),
  dismiss: (name) =>
    api.post(`/idea-video-styles/dismiss`, { name }).then((r) => r.data),
};

export const expenseCategoriesApi = {
  list: (includeArchived = false) =>
    api
      .get(`/expense-categories`, { params: includeArchived ? { include_archived: true } : {} })
      .then((r) => r.data),
  create: (name) =>
    api.post(`/expense-categories`, { name }).then((r) => r.data),
  update: (id, patch) =>
    api.patch(`/expense-categories/${id}`, patch).then((r) => r.data),
  remove: (id) => api.delete(`/expense-categories/${id}`).then((r) => r.data),
};

export const filesApi = {
  list: (projectId, folderId) =>
    api
      .get(`/projects/${projectId}/files`, {
        params: folderId === undefined ? {} : { folder_id: folderId },
      })
      .then((r) => r.data),
  upload: (projectId, file, onProgress, folderId) => {
    const form = new FormData();
    form.append("file", file);
    return api
      .post(`/projects/${projectId}/files`, form, {
        params: folderId ? { folder_id: folderId } : {},
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onProgress,
      })
      .then((r) => r.data);
  },
  move: (projectId, fileId, folderId) =>
    api
      .post(`/projects/${projectId}/files/${fileId}/move`, {
        folder_id: folderId,
      })
      .then((r) => r.data),
  setPublic: (projectId, fileId, isPublic) =>
    api
      .patch(`/projects/${projectId}/files/${fileId}`, { is_public: isPublic })
      .then((r) => r.data),
  remove: (projectId, fileId) =>
    api.delete(`/projects/${projectId}/files/${fileId}`).then((r) => r.data),
  bulkDelete: (projectId, fileIds) =>
    api
      .post(`/projects/${projectId}/files/bulk-delete`, { file_ids: fileIds })
      .then((r) => r.data),
  reorder: (projectId, orderedIds) =>
    api
      .post(`/projects/${projectId}/files/reorder`, { ordered_ids: orderedIds })
      .then((r) => r.data),
  bulkDownloadZip: (projectId, fileIds) =>
    api
      .post(
        `/projects/${projectId}/files/download-zip`,
        { file_ids: fileIds },
        { responseType: "blob" }
      )
      .then((r) => r),
  downloadUrl: (projectId, fileId) => {
    const t = getStoredToken();
    const params = new URLSearchParams({ disposition: "attachment" });
    if (t) params.set("token", t);
    return `${API}/projects/${projectId}/files/${fileId}/download?${params.toString()}`;
  },
  previewUrl: (projectId, fileId) => {
    const t = getStoredToken();
    const params = new URLSearchParams({ disposition: "inline" });
    if (t) params.set("token", t);
    return `${API}/projects/${projectId}/files/${fileId}/download?${params.toString()}`;
  },
};

export const foldersApi = {
  list: (projectId) =>
    api.get(`/projects/${projectId}/folders`).then((r) => r.data),
  create: (projectId, name, parentFolderId = null) =>
    api
      .post(`/projects/${projectId}/folders`, {
        name,
        parent_folder_id: parentFolderId,
      })
      .then((r) => r.data),
  rename: (projectId, folderId, name) =>
    api
      .patch(`/projects/${projectId}/folders/${folderId}`, { name })
      .then((r) => r.data),
  move: (projectId, folderId, parentFolderId) =>
    api
      .post(`/projects/${projectId}/folders/${folderId}/move`, {
        parent_folder_id: parentFolderId,
      })
      .then((r) => r.data),
  remove: (projectId, folderId) =>
    api
      .delete(`/projects/${projectId}/folders/${folderId}`)
      .then((r) => r.data),
};

export const attentionApi = {
  scores: () =>
    api.get(`/launchpad/attention-scores`).then((r) => r.data),
};

export const videosApi = {
  list: (projectId) =>
    api.get(`/videos`, { params: { project_id: projectId } }).then((r) => r.data),
  // Legacy path (kept for reference / small fallback). New uploads go via
  // tusInit + tusComplete so browser talks to Bunny directly, bypassing our
  // ingress body-size limits.
  upload: (projectId, file, title, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    return api
      .post(`/videos/upload`, form, {
        params: { project_id: projectId, ...(title ? { title } : {}) },
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: onProgress,
        timeout: 0,
      })
      .then((r) => r.data);
  },
  tusInit: (projectId, title) =>
    api
      .post(`/videos/tus-init`, { project_id: projectId, title })
      .then((r) => r.data),
  tusComplete: (payload) =>
    api.post(`/videos/tus-complete`, payload).then((r) => r.data),
  refresh: (videoId) =>
    api.post(`/videos/${videoId}/refresh`).then((r) => r.data),
  update: (videoId, updates) =>
    api.patch(`/videos/${videoId}`, updates).then((r) => r.data),
  remove: (videoId) => api.delete(`/videos/${videoId}`).then((r) => r.data),
};

export const photosApi = {
  list: (projectId) =>
    api.get(`/dam/photos`, { params: { project_id: projectId } }).then((r) => r.data),
  uploadInit: (payload) =>
    api.post(`/dam/photos/upload-init`, payload).then((r) => r.data),
  confirm: (payload) =>
    api.post(`/dam/photos/confirm`, payload).then((r) => r.data),
  downloadUrl: (photoId, size) =>
    api
      .get(`/dam/photos/${photoId}/download-url`, { params: { size } })
      .then((r) => r.data),
  remove: (photoId) =>
    api.delete(`/dam/photos/${photoId}`).then((r) => r.data),
};
